import {
  EGiftRegistryContributionStatus,
  EGiftRegistryItemStatus,
} from '@mahawi/eshop-common';
import {
  type IGiftRegistryAdminItem,
  type IGiftRegistryAdminItemContribution,
} from '@mahawi/eshop-common/src/types';
import {
  Button,
  DatePicker,
  Drawer,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../../fragments/date-format';
import { EMPTY_VALUE } from '../../fragments/empty-value';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import {
  giftRegistryDetailExtendDeadline,
  giftRegistryDetailLoad,
} from '../../reducers/gift-registry/reducer';
import { SPACING } from '../../theme/tokens';
import CopyToClipboard from '../copy-to-clipboard';

const { Text, Title } = Typography;

const ITEM_STATUS_COLOR: Record<EGiftRegistryItemStatus, string> = {
  [EGiftRegistryItemStatus.ACTIVE]: 'blue',
  [EGiftRegistryItemStatus.CONFIRMED]: 'green',
  [EGiftRegistryItemStatus.EXPIRED]: 'orange',
  [EGiftRegistryItemStatus.REFUNDED]: 'default',
};

const CONTRIBUTION_STATUS_COLOR: Record<
  EGiftRegistryContributionStatus,
  string
> = {
  [EGiftRegistryContributionStatus.PENDING]: 'default',
  [EGiftRegistryContributionStatus.CAPTURED]: 'green',
  [EGiftRegistryContributionStatus.CANCELLED]: 'red',
  [EGiftRegistryContributionStatus.APPLIED]: 'blue',
  [EGiftRegistryContributionStatus.REFUNDED]: 'orange',
};

function GiftRegistryDetailDrawer({
  open,
  onClose,
  registryUUID,
}: {
  open: boolean;
  onClose: () => void;
  registryUUID: string | null;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const detail = useSelector((s: RootState) => s.GiftRegistry.detail);
  const inProcess = useSelector((s: RootState) => s.GiftRegistry.inProcess);

  const [newExpiresAt, setNewExpiresAt] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (open && registryUUID) {
      dispatch(giftRegistryDetailLoad(registryUUID));
    }
  }, [open, registryUUID, dispatch]);

  useEffect(() => {
    setNewExpiresAt(null);
  }, [registryUUID]);

  return (
    <Drawer
      title={detail ? detail.title : 'Gift registry'}
      open={open}
      onClose={onClose}
      size={640}
    >
      {detail && (
        <Space
          size={SPACING.lg}
          orientation="vertical"
          style={{ width: '100%' }}
        >
          <Space orientation="vertical">
            <Text>
              Owner: {detail.ownerDisplayName} ({detail.ownerAccountEmail})
            </Text>
            <Text>
              Visibility: <Tag>{detail.isPublic ? 'Public' : 'Private'}</Tag>
            </Text>
            <Text>
              Country/Currency: {detail.countryType.isoCode} /{' '}
              {detail.currencyType.isoCode}
            </Text>
            <Text>
              Share token:{' '}
              <CopyToClipboard text={detail.shareToken} size="small" />{' '}
              {detail.shareToken}
            </Text>
          </Space>

          <Space align="center">
            <Text>
              Expires:{' '}
              {dayjs(detail.expiresAt).format(EDateFormat.DATE_TIME_SHORT)}
            </Text>
            <DatePicker
              showTime
              value={newExpiresAt}
              onChange={(value): void => setNewExpiresAt(value)}
              disabled={inProcess}
            />
            <Button
              size="small"
              disabled={!newExpiresAt || inProcess}
              onClick={(): void => {
                if (!newExpiresAt) {
                  return;
                }
                dispatch(
                  giftRegistryDetailExtendDeadline(
                    detail.uuid,
                    newExpiresAt.toDate(),
                  ),
                );
              }}
            >
              Extend deadline
            </Button>
          </Space>

          <Title level={5}>Items</Title>
          <Table<IGiftRegistryAdminItem>
            rowKey="uuid"
            loading={inProcess}
            dataSource={detail.items}
            pagination={false}
            columns={[
              {
                title: 'Product',
                dataIndex: ['product', 'name'],
                render: (
                  name: string | null,
                  record: IGiftRegistryAdminItem,
                ): string => name ?? record.product.uuid,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                render: (
                  status: EGiftRegistryItemStatus,
                ): React.ReactElement => (
                  <Tag color={ITEM_STATUS_COLOR[status]}>{status}</Tag>
                ),
              },
              {
                title: 'Target price',
                dataIndex: 'targetPrice',
                render: (price: number | null): string =>
                  price === null
                    ? EMPTY_VALUE
                    : `${price} ${detail.currencyType.isoCode}`,
              },
              {
                title: 'Collected',
                dataIndex: 'collectedAmount',
                render: (amount: number): string =>
                  `${amount} ${detail.currencyType.isoCode}`,
              },
            ]}
            expandable={{
              expandedRowRender: (
                record: IGiftRegistryAdminItem,
              ): React.ReactElement => (
                <Table<IGiftRegistryAdminItemContribution>
                  rowKey="uuid"
                  dataSource={record.contributions}
                  pagination={false}
                  size="small"
                  columns={[
                    { title: 'Donor', dataIndex: 'donorName' },
                    { title: 'Email', dataIndex: 'donorEmail' },
                    {
                      title: 'Amount',
                      dataIndex: 'amount',
                      render: (amount: number): string =>
                        `${amount} ${detail.currencyType.isoCode}`,
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      render: (
                        status: EGiftRegistryContributionStatus,
                      ): React.ReactElement => (
                        <Tag color={CONTRIBUTION_STATUS_COLOR[status]}>
                          {status}
                        </Tag>
                      ),
                    },
                    {
                      title: 'Captured at',
                      dataIndex: 'capturedAt',
                      render: (capturedAt: Date | null): string =>
                        capturedAt
                          ? dayjs(capturedAt).format(
                              EDateFormat.DATE_TIME_SHORT,
                            )
                          : EMPTY_VALUE,
                    },
                  ]}
                />
              ),
            }}
          />
        </Space>
      )}
    </Drawer>
  );
}

export default GiftRegistryDetailDrawer;
